import type { Ref } from 'react';
/* eslint-disable no-nested-ternary */
import { Grid } from 'antd';
import Select, { Props as SelectProps, components, MenuListProps } from 'react-select';
import { COLOR_ERROR, COLOR_PRIMARY_200, useTheme } from '..';
import { Spinner } from './Spinner';

const { useBreakpoint } = Grid;

export type AsyncSelectProps = SelectProps & {
  desktopHeight?: number | string;
  mobileHeight?: number | string;
  ref?: Ref<any> | undefined;
};

const MenuList = ({ children, ...props }: MenuListProps) => (
  <components.MenuList {...props}>
    {children}
    {props.selectProps.isLoading && <Spinner size={8} className="text-base-700" />}
  </components.MenuList>
);

export const AsyncSelect = ({
  ref,
  mobileHeight,
  desktopHeight = 0,
  ...props
}: AsyncSelectProps) => {
  const { xs, sm, md } = useBreakpoint();
  const { styles, ...restProps } = props;
  const { bgColor } = useTheme();

  const publicPageInputStyles = md ? desktopHeight : sm ? 40 : xs ? mobileHeight ?? 40 : 0;
  const commonInputStyles = md ? 0 : sm ? 32 : xs ? 32 : 'auto';
  return (
    <Select
      ref={ref}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          borderRadius: 100,
          minHeight: mobileHeight ? publicPageInputStyles : commonInputStyles,
          border: props['aria-invalid'] ? `1px solid ${COLOR_ERROR}` : '1px solid #d9d9d9',

          ':focus': {
            border: `1px solid ${bgColor}`,
            boxShadow: ' 0 0 0 2px rgba(10, 64, 137, 0.11)',
            outline: 0,
          },
          ':hover': {
            borderColor: `${bgColor}`,
            boxShadow: ' 0 0 0 2px rgba(10, 64, 137, 0.11)',
          },
          ':active': {
            borderColor: `${bgColor}`,
            boxShadow: ' 0 0 0 2px rgba(10, 64, 137, 0.11)',
          },
        }),

        indicatorSeparator: () => ({ display: 'none' }),
        loadingIndicator: () => ({ display: 'none' }),
        clearIndicator: (baseStyles) => ({
          ...baseStyles,
          padding: '0 4px',
        }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          padding: '0 4px',
          paddingRight: 8,
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          padding: '0 8px',
        }),

        option: (baseStyles, { isFocused, isSelected }) => {
          return {
            ...baseStyles,
            backgroundColor: isSelected
              ? COLOR_PRIMARY_200
              : isFocused
              ? 'rgba(0, 0, 0, 0.04)'
              : undefined,
            color: isSelected ? 'rgba(0, 0, 0, 0.88)' : undefined,
            fontWeight: isSelected ? 600 : undefined,
            padding: sm ? '6px 12px' : '10px 12px',
            borderRadius: 30,
            fontSize: sm ? 14 : 16,
          };
        },

        menuList: (baseStyles) => ({ ...baseStyles, padding: '10px 5px' }),
        menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999, borderRadius: 100 }),
        menu: (baseStyles) => ({
          ...baseStyles,
          ...(styles?.menu ?? {
            zIndex: 9999,
            bottom: 'auto',
            top: '100%',
            borderRadius: 30,
          }),
        }),
        placeholder: (baseStyles) => ({ ...baseStyles, color: '#00000040', opacity: 1 }),
      }}
      components={{ MenuList }}
      className="aria-invalid:border-red"
      {...restProps}
    />
  );
};
